// ============================================================
// DiffGamme — ce que la révision CHANGE, avant qu'elle change quoi que
// ce soit.
// ------------------------------------------------------------
// §8.6 : « L'écran montre le diff opération par opération, jamais un
// remplacement silencieux. »
//
// ── Pourquoi ça compte plus qu'il n'y paraît ──
//
// Une suggestion qui s'applique toute seule ne se conteste pas : le
// deviseur découvre le résultat, ne sait pas ce qui a bougé, et sa seule
// défense est de tout refaire à la main. À la deuxième fois il n'écrit
// plus de consigne du tout — et la boucle d'apprentissage meurt là,
// silencieusement, sans qu'aucun indicateur ne le montre.
//
// Le diff est calculé par `shared/gamme.js`, en code. On ne demande pas
// au modèle ce qu'il a changé : il raconterait ce qu'il croit avoir
// changé, et la comparaison de deux listes a une réponse exacte.
//
// ── Ce que la ligne de résumé porte ──
//
// L'effet sur les TROIS temps. C'est le seul chiffre qui dit si la
// révision vaut la peine d'être appliquée — « + 8 min de cycle, − 20 min
// de montage » se juge en une seconde, « 3 opérations modifiées » non.

import Button from '../../ui/Button.jsx';
import { LIBELLE_CATEGORIE } from '@shared/gamme.js';
import Badge from '../../ui/Badge.jsx';
import Puces from '../../ui/Puces.jsx';
import { formatMesure } from '../../lib/format.js';

const STATUTS = {
  ajoutee: { tone: 'ok', label: 'ajoutée' },
  retiree: { tone: 'danger', label: 'retirée' },
  modifiee: { tone: 'warn', label: 'modifiée' },
  inchangee: { tone: 'neutral', label: 'inchangée' },
};

/** Un écart de minutes, signé. « + 8 min », « − 20 min », ou rien. */
function Ecart({ valeur, label }) {
  if (!valeur) return null;
  return (
    <span className="diff__ecart">
      {label} <strong>{valeur > 0 ? '+' : '−'} {formatMesure(Math.abs(valeur), 'min')}</strong>
    </span>
  );
}

export function DiffGamme({
  diff, consigne, busy, onAbandonner, onEnFaireUneRegle = null, regleCreee = false,
  epingles = [],
}) {
  if (!diff?.lignes?.length) return null;
  const r = diff.resume;
  // Les lignes inchangées sont repliées : sur une gamme de huit
  // opérations dont deux bougent, en montrer huit noie les deux.
  const bougent = diff.lignes.filter((l) => l.statut !== 'inchangee');

  return (
    // ── LE DIFF SE CONSULTE, IL NE DÉCIDE PLUS ──
    //
    // Il portait un bouton « Appliquer à la gamme », et rien ne bougeait
    // tant qu'on ne cliquait pas. Depuis le 12 août la consigne réaligne
    // tout immédiatement : ce panneau ne sert plus qu'à VÉRIFIER ce
    // qu'elle a changé.
    //
    // Il est donc REPLIÉ. Ce n'est pas une décision à prendre au milieu
    // de l'écran, c'est un contrôle qu'on ouvre si on en a besoin — et
    // l'écran, lui, montre déjà la dernière gamme et les dernières
    // valeurs.
    <details className="diff" open={false}>
      <summary className="diff__resume-titre">
        Ce que votre consigne a changé —{' '}
        {r.ajoutees} ajoutée(s), {r.retirees} retirée(s), {r.modifiees} modifiée(s)
      </summary>
      <div className="row row--between">
        <p className="field__hint">
          La gamme, les temps et le prix sont <strong>déjà à jour</strong>. Rien n’est définitif
          tant que le devis n’est pas enregistré.
        </p>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onAbandonner}>
          Masquer
        </Button>
      </div>

      {consigne ? <p className="diff__consigne">« {consigne} »</p> : null}

      {/* ── « POUR NE PLUS REFAIRE L'ERREUR » ──
          C'est le chaînon qui manquait, et c'est le plus important de
          toute cette fonction.

          Une consigne stockée sur la pièce n'est relue que pour les
          pièces de signature PROCHE — même matière, même famille de
          référence, même forme. Or « le NLX reprend en contre-broche, je
          ne démonte pas » n'est PAS une propriété de cette pièce : c'est
          une propriété de l'atelier. Elle vaut pour toutes les pièces,
          et elle ne serait retrouvée que sur les comparables.

          Une RÈGLE, elle, est chargée à CHAQUE estimation. C'est la
          différence entre un post-it collé sur une pièce et quelque
          chose que le programme sait.

          Elle naît INACTIVE : la validation est un geste distinct, dans
          les Paramètres, et la phrase exacte est conservée comme source
          de vérité — jamais sa reformulation (§7bis). */}
      {onEnFaireUneRegle && consigne ? (
        <p className="field__hint">
          {regleCreee ? (
            <>
              <strong>Règle d’atelier créée.</strong> Elle est en attente de validation dans
              Paramètres → Règles métier. Une fois validée, elle sera lue à <em>chaque</em>
              {' '}estimation, sur toutes les pièces — plus seulement les comparables.
            </>
          ) : (
            <>
              Cette phrase vaut-elle pour <strong>toutes</strong> vos pièces, et pas seulement
              celle-ci ?{' '}
              <Button size="sm" variant="ghost" disabled={busy} onClick={onEnFaireUneRegle}>
                En faire une règle d’atelier
              </Button>
            </>
          )}
        </p>
      ) : null}

      {/* ── Ce qui a été RESTAURÉ se dit ──
          L'agent a reproposé un temps que vous aviez corrigé à la main :
          le programme a gardé le vôtre. Le faire en silence serait un
          second mensonge, dans l'autre sens — et vous ne sauriez pas que
          l'agent n'est pas d'accord avec vous, ce qui est justement une
          information. */}
      {epingles.length ? (
        <p className="field__hint" role="status">
          <strong>{epingles.length} temps que vous aviez corrigé(s) à la main ont été
          conservés.</strong>{' '}
          {epingles.map((e) => `${e.libelle} : l’agent propose ${e.propose} min, le vôtre (${e.retenu} min) est gardé`).join(' · ')}.
        </p>
      ) : null}

      <p className="field__hint">
        {r.ajoutees} ajoutée(s) · {r.retirees} retirée(s) · {r.modifiees} modifiée(s) ·{' '}
        {r.inchangees} inchangée(s)
        {/* L'effet sur les trois temps : le seul chiffre qui dit si ça
            vaut la peine d'appliquer. */}
        {/* Les écarts sur les totaux AFFICHÉS. Ils portaient les noms de
            l'ancien modèle et valaient donc zéro sur toute gamme
            d'aujourd'hui : le panneau annonçait « rien ne bouge » sur une
            révision qui changeait la machine et coupait 30 minutes. */}
        <Ecart valeur={r.prototype} label={`· ${LIBELLE_CATEGORIE.prototype.toLowerCase()}`} />
        <Ecart valeur={r.production} label={`· ${LIBELLE_CATEGORIE.production.toLowerCase()}`} />
        <Ecart valeur={r.par_lot} label="· par lot" />
      </p>

      {/* ── Ce qu'on affirme ici est ce qu'on SAIT ──
          La tentation était d'énumérer les causes possibles : « soit la
          gamme décrit déjà votre méthode, soit la consigne ne porte pas
          dessus ». On n'en sait rien — c'est un jugement de l'agent, pas
          un état du programme. Un « ou » dans un message veut dire qu'on
          n'a pas regardé, et le garde-fou du §C le refuse. On dit le
          fait, et le geste qui suit. */}
      {bougent.length === 0 ? (
        <p className="field__hint">
          <strong>L’agent n’a rien changé à la gamme.</strong> Sa lecture de votre consigne est
          dans les justifications ci-dessous. Précisez-la et relancez, ou corrigez la gamme
          directement.
        </p>
      ) : null}

      <ul className="diff__liste">
        {bougent.map((l, i) => {
          const op = l.apres ?? l.avant;
          const s = STATUTS[l.statut];
          return (
            <li key={`${op?.op_num ?? i}-${l.statut}`} className={`diff__ligne diff__ligne--${l.statut}`}>
              <span className="row">
                <Badge tone={s.tone}>{s.label}</Badge>
                <strong>
                  OP {op?.op_num} — {op?.libelle || 'sans libellé'}
                </strong>
                <span className="gamme__unite">
                  {formatMesure(op?.temps_min, 'min')} {op?.unite}
                </span>
              </span>

              {/* CHAMP PAR CHAMP : « temps 20 → 28 min ». Sans l'avant,
                  on ne peut pas juger l'après. */}
              {l.changements.length ? (
                <ul className="diff__champs">
                  {l.changements.map((c) => (
                    <li key={c.champ}>
                      {c.libelle} : <s>{String(c.avant ?? '—')}</s> → <strong>{String(c.apres ?? '—')}</strong>
                    </li>
                  ))}
                </ul>
              ) : null}

              {/* Le POURQUOI de l'agent : c'est ce qui permet de voir
                  qu'il a compris la consigne — ou qu'il ne l'a pas
                  comprise, et c'est encore plus utile. */}
              {l.statut !== 'retiree' ? (
                <Puces texte={l.apres?.justification} max={2} libelleReste="raison(s) de plus" />
              ) : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

export default DiffGamme;
